import { useState } from 'react';
import { api } from '../api';
import * as fmt from '../format';
import type { Store } from '../store';
import { Dialog } from './Dialog';

type Unit = 'steps' | 'miles';
type Mode = 'add' | 'set';

/// Manual entry, matching the native apps: both units a treadmill can show
/// you, and either adding to the day or replacing it.
export function RecordDialog({ store, onClose }: { store: Store; onClose: () => void }) {
  const asOf = store.summary?.asOf ?? fmt.todayKey();
  const [date, setDate] = useState(asOf);
  const [unit, setUnit] = useState<Unit>('steps');
  const [mode, setMode] = useState<Mode>('add');
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const row = store.days.find((d) => d.date === date);
  const perMile = store.walk?.stepsPerMile ?? 0;
  const entered = Number(value);
  const valid = value !== '' && Number.isFinite(entered) && entered >= 0;

  // A day recorded as an exact distance carries no step count, so adding steps
  // to one has to convert first or the distance is silently lost.
  const baseSteps = row?.steps ?? (perMile > 0 ? (row?.miles ?? 0) * perMile : 0);
  const projected =
    unit === 'steps'
      ? mode === 'set' ? entered : baseSteps + entered
      : mode === 'set' ? entered : (row?.miles ?? 0) + entered;

  async function save() {
    if (!valid || !store.walk) return;
    setSaving(true);
    setError(null);
    try {
      if (unit === 'steps') await api.setDaySteps(store.walk.walkId, date, projected);
      else await api.setDayMiles(store.walk.walkId, date, projected);
      await store.reload();
      onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog title="Record steps" onClose={onClose}>
      <label>
        Date
        <input type="date" value={date} max={fmt.todayKey()}
               min={store.walk?.startDate}
               onChange={(e) => setDate(e.target.value)} />
      </label>

      <fieldset>
        <legend>Units</legend>
        {(['steps', 'miles'] as const).map((option) => (
          <label key={option} className="inline">
            <input type="radio" name="unit" checked={unit === option}
                   onChange={() => setUnit(option)} />
            {option === 'steps' ? 'Steps' : 'Miles'}
          </label>
        ))}
      </fieldset>

      <fieldset>
        <legend>Mode</legend>
        {(['add', 'set'] as const).map((option) => (
          <label key={option} className="inline">
            <input type="radio" name="mode" checked={mode === option}
                   onChange={() => setMode(option)} />
            {option === 'add' ? 'Add to day' : 'Set day to'}
          </label>
        ))}
      </fieldset>

      <label>
        {unit === 'steps' ? 'Steps' : 'Miles'}
        <input type="number" inputMode="decimal" min="0" value={value}
               onChange={(e) => setValue(e.target.value)} />
      </label>

      <p className="muted">
        Currently{' '}
        {row
          ? row.steps !== null
            ? `${fmt.steps(row.steps)} steps · ${fmt.miles(row.miles)}`
            : fmt.miles(row.miles)
          : 'nothing recorded'}
        {valid && (
          <>
            {' → '}
            <strong>
              {unit === 'steps'
                ? `${fmt.steps(projected)} steps`
                : fmt.miles(projected)}
            </strong>
          </>
        )}
      </p>

      {error && <p className="error">{error}</p>}

      <div className="dialog__actions">
        <button onClick={onClose}>Cancel</button>
        <button className="primary" onClick={save} disabled={!valid || saving}>Save</button>
      </div>
    </Dialog>
  );
}
